/**
 * TECH IN DE LES - SHARED COMPONENTS
 * Reusable header, footer, and navigation components
 */

import { submitNewsletter } from './newsletter-emailjs.js';
import { submitContact } from './contact-emailjs.js';

// Configuration
const CONFIG = {
    siteName: 'Tech in de Les',
    navigation: [
        { label: 'Werkvormen', href: '/activities/' },
        { label: 'Workshops & Trainingen', href: '/workshops/' },
        { label: 'Blog', href: '/blog/' },
        { label: 'Contact', href: '/#contact' }
    ]
};

/**
 * Get the base path for relative URLs based on current page depth
 */
function getBasePath() {
    const path = window.location.pathname;

    // Count directory depth
    if (path.includes('/activities/html/')) {
        // Lesplan pages are two levels deep: activities/html/
        return '../..';
    }
    if (path.includes('/activities/') || path.includes('/about/') || path.includes('/blog/')) {
        return '..';
    }
    return '.';
}

/**
 * Get the root path (always navigates to site root, regardless of current depth)
 * Use this for navigation that should always go to root-level pages
 */
function getRootPath() {
    const path = window.location.pathname;

    // Count directory depth to get back to root
    if (path.includes('/activities/html/')) {
        // Lesplan pages are two levels deep: activities/html/
        return '../..';
    }
    if (path.includes('/activities/') || path.includes('/about/') || path.includes('/blog/') || path.includes('/workshops/')) {
        return '..';
    }
    return '.';
}

/**
 * Render the site header with navigation
 */
function renderHeader(options = {}) {
    const basePath = getRootPath();
    const currentPath = window.location.pathname;

    const header = document.createElement('header');
    header.className = 'site-header';

    // Generate navigation items
    const navItems = CONFIG.navigation.map(item => {
        // Determine if item is active
        let isActive = false;
        if (item.href.startsWith('/#')) {
            // Contact link - active on homepage
            isActive = currentPath === '/' || currentPath.endsWith('/index.html');
        } else if (item.href === '/blog/') {
            // Blog link - active on blog page
            isActive = currentPath.includes('/blog/');
        } else if (item.href === '/activities/') {
            // Werkvormen - active on activities page
            isActive = currentPath.includes('/activities/');
        } else if (item.href === '/workshops/') {
            isActive = currentPath.includes('/workshops/');
        }

        // Build href with basePath
        let href = item.href;
        if (!href.startsWith('http') && !href.startsWith('#')) {
            if (href.startsWith('/')) {
                href = `${basePath}${href}`;
            } else {
                href = `${basePath}/${href}`;
            }
        }

        return `<a href="${href}" class="${isActive ? 'active' : ''}">${item.label}</a>`;
    }).join('\n                    ');

    header.innerHTML = `
        <div class="container">
            <div class="header-content">
                <a href="${basePath}/index.html" class="logo">
                    ${CONFIG.siteName}
                </a>

                <button class="mobile-menu-toggle" aria-label="Menu">
                    ☰
                </button>

                <nav class="header-nav" id="mainNav">
                    ${navItems}
                </nav>
            </div>
        </div>
    `;

    // Add mobile menu toggle functionality
    const toggle = header.querySelector('.mobile-menu-toggle');
    const nav = header.querySelector('.header-nav');

    toggle.addEventListener('click', () => {
        nav.classList.toggle('active');
        toggle.textContent = nav.classList.contains('active') ? '✕' : '☰';
    });

    return header;
}

/**
 * Render the site footer
 */
function renderFooter(options = {}) {
    const footer = document.createElement('footer');
    footer.className = 'site-footer';

    const basePath = getBasePath();

    footer.innerHTML = `
        <hr>
        <div class="footer-content">
            <p class="footer-copyright">© 2025 - Tech in de Les / Bert van Uffelen</p>
            <div class="footer-links">
                <a href="${basePath}/blog/privacy-policy.html">Privacy & Cookies</a>
            </div>
            <div class="footer-social">
                <a href="https://linkedin.com/in/bvanuffelen" target="_blank" rel="noopener" aria-label="LinkedIn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                        <rect x="2" y="9" width="4" height="12"></rect>
                        <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                </a>
                <a href="https://www.youtube.com/@Techindeles" target="_blank" rel="noopener" aria-label="YouTube">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path>
                        <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon>
                    </svg>
                </a>
            </div>
        </div>
    `;

    return footer;
}

/**
 * Initialize shared components on page load
 * Call this function to automatically inject header and footer
 */
function initSharedComponents(options = {}) {
    const {
        headerTarget = '#site-header',
        footerTarget = '#site-footer',
        contactTarget = '#contact-placeholder',
        newsletterTarget = '#newsletter-placeholder',
        injectStyles = true,
        contactConfig = {},
        newsletterConfig = null
    } = options;

    // Inject shared styles if not already present
    if (injectStyles && !document.querySelector('link[href*="shared/styles.css"]')) {
        const basePath = getBasePath();
        const styleLink = document.createElement('link');
        styleLink.rel = 'stylesheet';
        styleLink.href = `${basePath}/shared/styles.css`;
        document.head.appendChild(styleLink);
    }

    // Render header
    const headerTarget_el = document.querySelector(headerTarget);
    if (headerTarget_el) {
        const header = renderHeader(options);
        headerTarget_el.replaceWith(header);
    }

    // Render footer
    const footerTarget_el = document.querySelector(footerTarget);
    if (footerTarget_el) {
        const footer = renderFooter(options);
        footerTarget_el.replaceWith(footer);
    }

    // Render contact
    const contactTarget_el = document.querySelector(contactTarget);
    if (contactTarget_el) {
        const contact = renderContact(contactConfig);
        contactTarget_el.replaceWith(contact);
    }

    // Render newsletter
    const newsletterTarget_el = document.querySelector(newsletterTarget);
    if (newsletterTarget_el && newsletterConfig) {
        const newsletter = renderNewsletter(newsletterConfig);
        newsletterTarget_el.replaceWith(newsletter);
    }
}

/**
 * Render a page hero section
 * @param {Object} config - Hero configuration
 * @param {string} config.title - Main title (required)
 * @param {string} [config.subtitle] - Optional subtitle
 * @param {string} [config.backgroundImage] - Optional background image URL
 * @param {string} [config.backgroundPosition='center'] - Background position (e.g., 'top', 'center', 'bottom')
 * @param {Array} [config.buttons] - Optional array of button objects
 * @param {string} config.buttons[].text - Button text
 * @param {string} config.buttons[].href - Button link (# for scroll, / for internal, https for external)
 * @param {string} [config.buttons[].type='primary'] - Button type: 'primary' or 'secondary'
 * @param {boolean} [config.buttons[].external=false] - Opens in new tab if true
 */
function renderPageHero(config) {
    const container = document.getElementById('page-hero');
    if (!container) return;

    if (!config || !config.title) {
        console.warn('renderPageHero: title is required');
        return;
    }

    const basePath = getBasePath();

    // Build background style if image is provided
    let backgroundStyle = '';
    if (config.backgroundImage) {
        // Handle relative URLs for background image
        let bgUrl = config.backgroundImage;
        if (bgUrl && !bgUrl.startsWith('http') && !bgUrl.startsWith('/')) {
            bgUrl = `${basePath}/${bgUrl}`;
        } else if (bgUrl && bgUrl.startsWith('/')) {
            bgUrl = `${basePath}${bgUrl}`;
        }
        const bgPosition = config.backgroundPosition || 'center';
        backgroundStyle = ` style="background: linear-gradient(rgba(26, 26, 46, 0.65), rgba(15, 15, 35, 1)), url('${bgUrl}') ${bgPosition}/cover no-repeat;"`;
    }

    // Build subtitle HTML
    const subtitleHtml = config.subtitle
        ? `<p class="page-hero__subtitle">${config.subtitle}</p>`
        : '';

    // Build buttons HTML
    let buttonsHtml = '';
    if (config.buttons && config.buttons.length > 0) {
        const buttonItems = config.buttons.map(btn => {
            const externalAttr = btn.external ? ' target="_blank" rel="noopener"' : '';
            const btnType = btn.type || 'primary';

            // Handle relative URLs
            let href = btn.href;
            if (href && !href.startsWith('#') && !href.startsWith('http') && !href.startsWith('/')) {
                href = `${basePath}/${href}`;
            } else if (href && href.startsWith('/')) {
                href = `${basePath}${href}`;
            }

            return `<a href="${href}" class="btn btn--${btnType}"${externalAttr}>${btn.text}</a>`;
        }).join('\n            ');

        buttonsHtml = `
        <div class="page-hero__buttons">
            ${buttonItems}
        </div>`;
    }

    // Render the hero section
    container.innerHTML = `
        <section class="page-hero"${backgroundStyle}>
            <div class="page-hero__container">
                <h1 class="page-hero__title">${config.title}</h1>
                ${subtitleHtml}
                ${buttonsHtml}
            </div>
        </section>
    `;
}

// Expose renderPageHero globally
window.renderPageHero = renderPageHero;

/**
 * Render contact section with configurable contact details
 * @param {Object} config - Contact configuration
 * @param {string} [config.title='ONTDEK<br>DE<br>MAGIE'] - Main title (HTML allowed)
 * @param {string} [config.phone='06 53 44 36 51'] - Phone number display
 * @param {string} [config.phoneHref='tel:+31653443651'] - Phone number href
 * @param {string} [config.whatsapp='+31653443651'] - WhatsApp number
 * @param {string} [config.linkedin='https://linkedin.com/in/bvanuffelen'] - LinkedIn URL
 * @param {string} [config.email='info@techindeles.nl'] - Email address
 * @param {string} [config.backgroundImage] - Optional background image URL
 * @param {string} [config.backgroundPosition='center'] - Background position (e.g., 'top', 'center', 'bottom')
 */
function renderContact(config = {}) {
    const {
        title = 'ONTDEK<br>DE<br>MAGIE',
        phone = '06 53 44 36 51',
        phoneHref = 'tel:+31653443651',
        whatsapp = '+31653443651',
        linkedin = 'https://linkedin.com/in/bvanuffelen',
        email = 'info@techindeles.nl',
        backgroundImage = null,
        backgroundPosition = 'center'
    } = config;

    const basePath = getBasePath();  // For logo image
    const rootPath = getRootPath();  // For navigation buttons (always go to root-level pages)
    const section = document.createElement('section');
    section.id = 'contact';
    section.style.cssText = 'padding: 60px 20px; background: var(--bg-default);';

    // Build background style
    let backgroundStyle = 'linear-gradient(135deg, var(--bg-dark-start) 0%, var(--bg-dark-mid) 50%, var(--bg-dark-end) 100%)';

    if (backgroundImage) {
        let bgUrl = backgroundImage;
        if (bgUrl && !bgUrl.startsWith('http') && !bgUrl.startsWith('/')) {
            bgUrl = `${basePath}/${bgUrl}`;
        } else if (bgUrl && bgUrl.startsWith('/')) {
            bgUrl = `${basePath}${bgUrl}`;
        }
        // Add gradient overlay over image
        backgroundStyle = `linear-gradient(rgba(26, 26, 46, 0.75), rgba(15, 15, 35, 0.85)), url('${bgUrl}') ${backgroundPosition}/cover no-repeat`;
    }

    section.innerHTML = `
        <div class="container" style="padding: 60px 40px; background: ${backgroundStyle}; border-radius: var(--radius-lg);">

            <!-- Logo & Buttons Row -->
            <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 20px; margin-bottom: 50px;">
                <img src="${basePath}/public/images/logo-soundgrid-v1.svg" alt="SoundGrid Logo" style="height: 50px;">
                <div style="display: flex; gap: 15px; flex-wrap: wrap;">
                    <a href="${rootPath}/activities/" class="btn" style="background: var(--primary); color: white; border: 2px solid var(--secondary); padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 500; font-size: 0.95rem; transition: background 0.3s;">Werkvormen</a>
                    <a href="${rootPath}/workshops/" class="btn" style="background: var(--primary); color: white; border: 2px solid var(--secondary); padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 500; font-size: 0.95rem; transition: background 0.3s;">Workshops</a>
                    <a href="${rootPath}/blog/" class="btn" style="background: var(--primary); color: white; border: 2px solid var(--secondary); padding: 12px 24px; border-radius: 8px; text-decoration: none; font-weight: 500; font-size: 0.95rem; transition: background 0.3s;">Blog</a>
                </div>
            </div>

            <!-- Contact Grid -->
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: start; --contact-cols: 2;" class="contact-grid">

                <!-- Left: Title -->
                <div>
                    <h2 style="font-family: var(--font-heading); font-size: 3.5rem; font-weight: 700; color: var(--text-on-dark); line-height: 1.1; text-transform: uppercase; margin: 0;">
                        ${title}
                    </h2>
                </div>

                <!-- Right: Contact Links -->
                <div>
                    <h3 style="font-family: var(--font-heading); font-size: 1.8rem; font-weight: 600; color: var(--text-on-dark); margin-bottom: 24px; text-transform: uppercase;">
                        Neem contact op
                    </h3>

                    <div style="display: flex; flex-direction: column; gap: 16px;">

                        <!-- Phone -->
                        <a href="${phoneHref}" style="display: flex; align-items: center; gap: 12px; text-decoration: none; color: var(--text-on-dark); padding: 8px 0;">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                            </svg>
                            <div>
                                <div style="font-weight: 600; font-size: 1rem; margin-bottom: 2px;">Telefoon</div>
                                <div style="color: var(--text-on-dark-muted); font-size: 0.9rem;">${phone}</div>
                            </div>
                        </a>

                        <!-- WhatsApp -->
                        <a href="https://wa.me/${whatsapp}" target="_blank" style="display: flex; align-items: center; gap: 12px; text-decoration: none; color: var(--text-on-dark); padding: 8px 0;">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                            </svg>
                            <div>
                                <div style="font-weight: 600; font-size: 1rem; margin-bottom: 2px;">WhatsApp</div>
                                <div style="color: var(--text-on-dark-muted); font-size: 0.9rem;">Start een gesprek</div>
                            </div>
                        </a>

                        <!-- LinkedIn -->
                        <a href="${linkedin}" target="_blank" style="display: flex; align-items: center; gap: 12px; text-decoration: none; color: var(--text-on-dark); padding: 8px 0;">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
                                <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                                <rect x="2" y="9" width="4" height="12"></rect>
                                <circle cx="4" cy="4" r="2"></circle>
                            </svg>
                            <div>
                                <div style="font-weight: 600; font-size: 1rem; margin-bottom: 2px;">LinkedIn</div>
                                <div style="color: var(--text-on-dark-muted); font-size: 0.9rem;">Verbind op LinkedIn</div>
                            </div>
                        </a>

                        <!-- Email -->
                        <a href="mailto:${email}" style="display: flex; align-items: center; gap: 12px; text-decoration: none; color: var(--text-on-dark); padding: 8px 0;">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="flex-shrink: 0;">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                <polyline points="22,6 12,13 2,6"></polyline>
                            </svg>
                            <div>
                                <div style="font-weight: 600; font-size: 1rem; margin-bottom: 2px;">Email</div>
                                <div style="color: var(--text-on-dark-muted); font-size: 0.9rem;">${email}</div>
                            </div>
                        </a>

                    </div>
                </div>

            </div>

            <!-- Horizontal Divider -->
            <div style="width: 100%; height: 1px; background: var(--secondary); margin: 60px 0;"></div>

            <!-- Contact Form Section -->
            <div class="contact-form-section">
                <h3 style="font-family: var(--font-heading); font-size: 1.75rem; color: var(--text-on-dark); margin-bottom: 1rem; text-align: center;">
                    Stuur een bericht
                </h3>
                <p style="font-family: var(--font-body); font-size: 1rem; color: var(--text-on-dark-muted); margin-bottom: 2rem; text-align: center; max-width: 600px; margin-left: auto; margin-right: auto;">
                    Vul het onderstaande formulier in en we nemen zo snel mogelijk contact met je op.
                </p>

                <form class="contact-form" style="max-width: 600px; margin: 0 auto;">
                    <!-- Name Input -->
                    <div style="margin-bottom: 1rem;">
                        <label for="contact-name" style="display: block; font-family: var(--font-body); font-size: 0.875rem; font-weight: 500; color: var(--text-on-dark); margin-bottom: 0.5rem;">
                            Naam *
                        </label>
                        <input
                            type="text"
                            id="contact-name"
                            name="name"
                            required
                            placeholder="Je naam"
                            style="width: 100%; padding: 0.75rem 1rem; border: 2px solid rgba(255,255,255,0.2); border-radius: 8px; font-family: var(--font-body); font-size: 1rem; color: var(--text-on-dark); background: rgba(0,0,0,0.2); transition: border-color 0.3s ease; box-sizing: border-box;"
                        />
                    </div>

                    <!-- Email Input -->
                    <div style="margin-bottom: 1rem;">
                        <label for="contact-email" style="display: block; font-family: var(--font-body); font-size: 0.875rem; font-weight: 500; color: var(--text-on-dark); margin-bottom: 0.5rem;">
                            E-mail *
                        </label>
                        <input
                            type="email"
                            id="contact-email"
                            name="email"
                            required
                            placeholder="je@email.nl"
                            style="width: 100%; padding: 0.75rem 1rem; border: 2px solid rgba(255,255,255,0.2); border-radius: 8px; font-family: var(--font-body); font-size: 1rem; color: var(--text-on-dark); background: rgba(0,0,0,0.2); transition: border-color 0.3s ease; box-sizing: border-box;"
                        />
                    </div>

                    <!-- Message Textarea -->
                    <div style="margin-bottom: 1rem;">
                        <label for="contact-message" style="display: block; font-family: var(--font-body); font-size: 0.875rem; font-weight: 500; color: var(--text-on-dark); margin-bottom: 0.5rem;">
                            Bericht *
                        </label>
                        <textarea
                            id="contact-message"
                            name="message"
                            required
                            rows="5"
                            placeholder="Je bericht..."
                            style="width: 100%; padding: 0.75rem 1rem; border: 2px solid rgba(255,255,255,0.2); border-radius: 8px; font-family: var(--font-body); font-size: 1rem; color: var(--text-on-dark); background: rgba(0,0,0,0.2); transition: border-color 0.3s ease; resize: vertical; box-sizing: border-box;"
                        ></textarea>
                    </div>

                    <!-- Functional Consent Checkbox -->
                    <div class="form-group consent-group consent-group--dark">
                        <input
                            type="checkbox"
                            name="functional_consent"
                            id="contact-consent"
                            required
                            aria-label="Functionele cookies accepteren"
                        />
                        <label for="contact-consent">
                            Ik ga akkoord met het gebruik van functionele cookies om dit formulier te verzenden.
                            <a href="/privacy-policy.html" target="_blank" style="color: var(--secondary); text-decoration: underline;">
                                Lees ons privacybeleid
                            </a>
                        </label>
                    </div>

                    <!-- Submit Button -->
                    <button
                        type="submit"
                        class="submit-btn"
                        style="width: 100%; padding: 12px 24px; background: var(--primary); color: white; border: 2px solid var(--secondary); border-radius: 8px; font-size: 0.95rem; font-weight: 500; cursor: pointer; transition: background 0.3s; display: flex; align-items: center; justify-content: center; gap: 0.5rem;"
                    >
                        <span class="btn-text">Verstuur bericht</span>
                        <span class="btn-loading" style="display: none;">
                            <span style="display: inline-block; width: 16px; height: 16px; border: 2px solid white; border-top-color: transparent; border-radius: 50%; animation: spin 0.8s linear infinite;"></span>
                        </span>
                    </button>

                    <!-- Feedback Message -->
                    <div class="feedback-message" style="display: none; margin-top: 1rem; padding: 1rem; border-radius: 8px; font-family: var(--font-body); font-size: 0.875rem;"></div>
                </form>
            </div>

            <!-- CSS for form interactions -->
            <style>
                .contact-form input:focus,
                .contact-form textarea:focus {
                    outline: none;
                    border-color: var(--secondary) !important;
                }

                .contact-form .submit-btn:hover {
                    background: #404040 !important;
                }

                .contact-form .submit-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .feedback-message.success {
                    background: #F0FDF4;
                    border: 2px solid #22C55E;
                    color: #166534;
                }

                .feedback-message.error {
                    background: #FEF2F2;
                    border: 2px solid #EF4444;
                    color: #991B1B;
                }

                .feedback-message.info {
                    background: #EFF6FF;
                    border: 2px solid #3B82F6;
                    color: #1E40AF;
                }

                /* .consent-group styling is now in shared/styles.css */
                /* Extra margin for contact form consent */
                .contact-form .consent-group {
                    margin-bottom: 1.5rem;
                }

                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .contact-form-section h3 {
                        font-size: 1.5rem;
                    }

                    .contact-form-section p {
                        font-size: 0.875rem;
                    }

                    .contact-form input,
                    .contact-form textarea {
                        font-size: 16px; /* Prevent iOS zoom on focus */
                    }
                }
            </style>

        </div>
    `;

    const contactSection = section;

    // Attach event listener to form after DOM is ready
    setTimeout(() => {
        const form = contactSection.querySelector('.contact-form');
        if (form) {
            form.addEventListener('submit', async (e) => {
                e.preventDefault();

                const submitBtn = form.querySelector('.submit-btn');
                const btnText = submitBtn.querySelector('.btn-text');
                const btnLoading = submitBtn.querySelector('.btn-loading');
                const feedbackEl = form.querySelector('.feedback-message');

                // Get form data
                const formData = {
                    name: form.name.value.trim(),
                    email: form.email.value.trim(),
                    message: form.message.value.trim()
                };

                const consentCheckbox = form.querySelector('input[name="functional_consent"]');
                const hasConsent = consentCheckbox ? consentCheckbox.checked : false;

                // Validate
                if (!formData.name || !formData.email || !formData.message) {
                    showContactFeedback(feedbackEl, 'error', 'Vul alsjeblieft alle verplichte velden in.');
                    return;
                }

                if (!hasConsent) {
                    showContactFeedback(feedbackEl, 'error', 'Je moet akkoord gaan met functionele cookies om dit formulier te versturen.');
                    return;
                }

                // Show loading state
                submitBtn.disabled = true;
                btnText.style.display = 'none';
                btnLoading.style.display = 'flex';
                feedbackEl.style.display = 'none';

                try {
                    await submitContact(formData);
                    showContactFeedback(feedbackEl, 'success', '✓ Bedankt! Je bericht is verzonden. We nemen zo snel mogelijk contact met je op.');
                    form.reset();
                } catch (error) {
                    if (error.message === 'NO_CONSENT') {
                        showContactFeedback(feedbackEl, 'info', 'Accepteer eerst functionele cookies om dit formulier te gebruiken.');
                    } else {
                        showContactFeedback(feedbackEl, 'error', 'Er ging iets mis. Probeer het later opnieuw.');
                        console.error('Contact form submission error:', error);
                    }
                } finally {
                    submitBtn.disabled = false;
                    btnText.style.display = 'inline';
                    btnLoading.style.display = 'none';
                }
            });
        }
    }, 0);

    return section;
}

/**
 * Show feedback message in contact form
 * @param {HTMLElement} feedbackEl - Feedback message element
 * @param {string} type - Message type ('success', 'error', 'info')
 * @param {string} message - Message text
 */
function showContactFeedback(feedbackEl, type, message) {
    feedbackEl.className = `feedback-message ${type}`;
    feedbackEl.textContent = message;
    feedbackEl.style.display = 'block';

    // Auto-hide success messages after 5 seconds
    if (type === 'success') {
        setTimeout(() => {
            feedbackEl.style.display = 'none';
        }, 5000);
    }
}

// Expose renderContact globally
window.renderContact = renderContact;

/**
 * Render newsletter section with configurable content
 * @param {Object} config - Newsletter configuration
 * @param {string} [config.title='Sluit je aan bij technologie in de muziekles liefhebbers'] - Newsletter title
 * @param {string} [config.description='Je ontvangt een frisse selectie van de beste tools...'] - Newsletter description
 * @param {string} [config.buttonText='Aanmelden'] - CTA button text
 * @param {string} [config.placeholder='Je mailadres'] - Email input placeholder
 * @param {string} [config.email='info@techindeles.nl'] - Email address for mailto
 * @param {string} [config.emailSubject='Aanmelding%20Nieuwsbrief'] - Email subject (URL encoded)
 * @param {string} [config.emailBody='Hallo%2C%0D%0A%0D%0AIk%20wil%20me%20graag%20aanmelden...'] - Email body (URL encoded)
 * @param {string} [config.backgroundColor=null] - Optional override for background gradient
 * @param {string} [config.textColor=null] - Optional override for text color
 * @param {string} [config.buttonColor=null] - Optional override for button color
 * @param {string} [config.buttonHoverColor=null] - Optional override for button hover color
 * @param {string} [config.gridRatio='60% 40%'] - Grid ratio for text/form split
 * @param {string} [config.padding='80px 0'] - Vertical padding
 * @param {string} [config.responsivePadding='40px 0'] - Mobile padding
 */
function renderNewsletter(config = {}) {
    const {
        title = 'Sluit je aan bij technologie in de muziekles liefhebbers',
        description = 'Je ontvangt een frisse selectie van de beste tools en werkvormen. Zodra ik nieuwe content ontdek, landen ze automatisch in je mailbox.',
        buttonText = 'Aanmelden',
        placeholder = 'Je mailadres',
        email = 'info@techindeles.nl',
        emailSubject = 'Aanmelding%20Nieuwsbrief',
        emailBody = 'Hallo%2C%0D%0A%0D%0AIk%20wil%20me%20graag%20aanmelden%20voor%20de%20nieuwsbrief.%0D%0A%0D%0AMijn%20e-mailadres%20is%3A%20',
        backgroundColor = null,
        textColor = null,
        buttonColor = null,
        buttonHoverColor = null,
        gridRatio = '60% 40%',
        padding = '80px 0',
        responsivePadding = '40px 0'
    } = config;

    // Build mailto URL
    const mailtoUrl = `mailto:${email}?subject=${emailSubject}&body=${emailBody}`;

    // Create section element
    const section = document.createElement('section');
    section.className = 'newsletter-section';

    // Build custom styles string (optional overrides)
    const customStyles = `
        ${backgroundColor ? `background: ${backgroundColor} !important;` : ''}
        ${textColor ? `color: ${textColor} !important;` : ''}
    `;

    // Build HTML template with inline styles
    section.innerHTML = `
        <style>
            .newsletter-section {
                background: linear-gradient(135deg, var(--bg-dark-start) 0%, var(--bg-dark-mid) 50%, var(--bg-dark-end) 100%);
                padding: ${padding};
                ${customStyles}
            }

            .newsletter-container {
                max-width: var(--container-max);
                margin: 0 auto;
                padding: 0 var(--container-padding);
            }

            .newsletter-grid {
                display: grid;
                grid-template-columns: ${gridRatio};
                gap: 60px;
                align-items: center;
            }

            .newsletter-grid > div {
                min-width: 0;
                overflow: hidden;
            }

            .newsletter-section h2 {
                font-family: var(--font-heading);
                font-size: clamp(2rem, 5vw, 3rem);
                font-weight: 700;
                color: white;
                margin-bottom: 20px;
            }

            .newsletter-section p {
                font-size: 1.2rem;
                color: rgba(255, 255, 255, 0.9);
                line-height: 1.6;
            }

            .newsletter-form {
                display: flex;
                flex-direction: column;
                gap: 16px;
                box-sizing: border-box;
            }

            .newsletter-form .form-group {
                width: 100%;
                margin: 0;
            }

            .newsletter-form input[type="text"],
            .newsletter-form input[type="email"] {
                width: 100%;
                padding: 16px 24px;
                border: 2px solid rgba(255, 255, 255, 0.2);
                border-radius: var(--radius-md);
                font-size: 1rem;
                font-family: var(--font-body);
                box-sizing: border-box;
                background: rgba(255, 255, 255, 0.95);
                transition: border-color 0.2s;
            }

            .newsletter-form input::placeholder {
                color: var(--text-secondary);
            }

            .newsletter-form input[type="text"]:focus,
            .newsletter-form input[type="email"]:focus {
                outline: none;
                border-color: white;
                background: white;
            }

            /* .consent-group styling is now in shared/styles.css */

            .newsletter-form .submit-btn {
                width: 100%;
                padding: 16px 32px;
                background: ${buttonColor || 'var(--secondary)'};
                color: white;
                border: none;
                border-radius: var(--radius-md);
                font-size: 1rem;
                font-weight: 600;
                font-family: var(--font-body);
                cursor: pointer;
                transition: all 0.3s;
                box-sizing: border-box;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
            }

            .newsletter-form .submit-btn:hover:not(:disabled) {
                background: ${buttonHoverColor || 'var(--accent)'};
                transform: translateY(-2px);
            }

            .newsletter-form .submit-btn:disabled {
                opacity: 0.7;
                cursor: not-allowed;
            }

            .newsletter-form .btn-loading {
                display: none;
                align-items: center;
                gap: 8px;
            }

            @keyframes spin {
                to { transform: rotate(360deg); }
            }

            .newsletter-form .spinner {
                animation: spin 1s linear infinite;
                width: 20px;
                height: 20px;
            }

            .feedback-message {
                display: none;
                margin-top: 12px;
                padding: 12px 16px;
                border-radius: 8px;
                font-size: 0.95rem;
                line-height: 1.5;
            }

            .feedback-success {
                background: rgba(209, 250, 229, 0.9);
                color: #065f46;
                border: 1px solid #059669;
            }

            .feedback-error {
                background: rgba(254, 226, 226, 0.9);
                color: #991b1b;
                border: 1px solid #dc2626;
            }

            .feedback-info {
                background: rgba(219, 234, 254, 0.9);
                color: #1e40af;
                border: 1px solid #3b82f6;
            }

            @media (max-width: 1200px) {
                .newsletter-grid {
                    grid-template-columns: 1fr;
                    gap: 40px;
                }

                .newsletter-form {
                    max-width: 500px;
                    margin: 0 auto;
                }
            }

            @media (max-width: 768px) {
                .newsletter-section {
                    padding: ${responsivePadding} !important;
                }

                .newsletter-container {
                    padding: 0 var(--container-padding);
                }

                .newsletter-grid {
                    grid-template-columns: 1fr;
                    gap: 30px;
                }

                .newsletter-section h2 {
                    font-size: clamp(1.5rem, 4vw, 2.5rem);
                }

                .newsletter-section p {
                    font-size: 1.1rem;
                }

                .newsletter-form {
                    width: 100%;
                    box-sizing: border-box;
                }

                .newsletter-form input:not([type="checkbox"]),
                .newsletter-form button {
                    width: 100%;
                    box-sizing: border-box;
                    font-size: 1rem;
                }

                .newsletter-form button {
                    padding: 14px 24px;
                }
            }

            @media (max-width: 480px) {
                .newsletter-container {
                    padding: 0 15px;
                }

                .newsletter-section h2 {
                    font-size: 1.5rem;
                    margin-bottom: 16px;
                }

                .newsletter-section p {
                    font-size: 0.95rem;
                    margin-bottom: 24px;
                }

                .newsletter-grid {
                    gap: 24px;
                }

                .newsletter-form input:not([type="checkbox"]),
                .newsletter-form button {
                    padding: 12px 20px;
                    font-size: 0.95rem;
                }

                .newsletter-form {
                    gap: 12px;
                }
            }
        </style>

        <div class="newsletter-container">
            <div class="newsletter-grid">
                <div>
                    <h2>${title}</h2>
                    <p>${description}</p>
                </div>
                <div>
                    <form class="newsletter-form" id="newsletter-form-${Date.now()}">
                        <div class="form-group">
                            <input
                                type="text"
                                name="name"
                                placeholder="Je naam *"
                                required
                                aria-label="Naam"
                            >
                        </div>
                        <div class="form-group">
                            <input
                                type="email"
                                name="email"
                                placeholder="${placeholder} *"
                                required
                                aria-label="Email"
                            >
                        </div>
                        <div class="form-group">
                            <input
                                type="text"
                                name="school"
                                placeholder="School (optioneel)"
                                aria-label="School"
                            >
                        </div>
                        <div class="consent-group consent-group--dark">
                            <input
                                type="checkbox"
                                id="newsletter-consent-${Date.now()}"
                                name="marketing_consent"
                                aria-label="Ik ga akkoord met informeren"
                            >
                            <label for="newsletter-consent-${Date.now()}">
                                Ik ga akkoord met het verstrekken van mijn gegevens en wil graag op de hoogte blijven van nieuwe content
                            </label>
                        </div>
                        <button type="submit" class="submit-btn">
                            <span class="btn-text">${buttonText}</span>
                            <span class="btn-loading" style="display:none;">
                                <svg class="spinner" width="20" height="20" viewBox="0 0 24 24">
                                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="4" opacity="0.25"/>
                                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" opacity="0.75"/>
                                </svg>
                                Versturen...
                            </span>
                        </button>
                        <div class="feedback-message" role="alert" aria-live="polite"></div>
                    </form>
                </div>
            </div>
        </div>
    `;

    // Add submit handler
    const form = section.querySelector('.newsletter-form');
    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = form.querySelector('.submit-btn');
        const btnText = submitBtn.querySelector('.btn-text');
        const btnLoading = submitBtn.querySelector('.btn-loading');
        const feedbackEl = form.querySelector('.feedback-message');

        // Get form data
        const formData = {
            name: form.name.value.trim(),
            email: form.email.value.trim(),
            school: form.school.value.trim() || 'Niet opgegeven'
        };

        const consentCheckbox = form.querySelector('input[name="marketing_consent"]');
        const hasConsent = consentCheckbox ? consentCheckbox.checked : false;

        // Validate
        if (!formData.name || !formData.email) {
            showNewsletterFeedback(feedbackEl, 'error', 'Vul alsjeblieft alle verplichte velden in.');
            return;
        }

        if (!hasConsent) {
            showNewsletterFeedback(feedbackEl, 'error', 'Je moet akkoord gaan met marketing voordat je je kunt aanmelden.');
            return;
        }

        // Show loading state
        submitBtn.disabled = true;
        btnText.style.display = 'none';
        btnLoading.style.display = 'flex';
        feedbackEl.style.display = 'none';

        try {
            // Submit via EmailJS
            await submitNewsletter(formData);

            // Success
            showNewsletterFeedback(feedbackEl, 'success', '✓ Bedankt! Je bent aangemeld voor de nieuwsbrief.');
            form.reset();

        } catch (error) {
            // Error handling
            if (error.message === 'NO_CONSENT') {
                showNewsletterFeedback(feedbackEl, 'info', 'Accepteer eerst marketing cookies om je aan te melden.');
            } else {
                showNewsletterFeedback(feedbackEl, 'error', 'Er ging iets mis. Probeer het later opnieuw.');
                console.error('Newsletter submission error:', error);
            }
        } finally {
            // Reset loading state
            submitBtn.disabled = false;
            btnText.style.display = 'inline';
            btnLoading.style.display = 'none';
        }
    });

    return section;
}

/**
 * Show newsletter feedback message
 */
function showNewsletterFeedback(element, type, message) {
    element.textContent = message;
    element.className = `feedback-message feedback-${type}`;
    element.style.display = 'block';
}

// Expose renderNewsletter globally
window.renderNewsletter = renderNewsletter;

/**
 * Utility: Smooth scroll to anchor
 */
function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
            e.preventDefault();
            const target = document.querySelector(this.getAttribute('href'));
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        });
    });
}

/**
 * Utility: Capitalize first letter
 */
function capitalizeFirst(str) {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
}

// Expose globally for module imports
window.renderHeader = renderHeader;
window.renderFooter = renderFooter;
window.renderContact = renderContact;
window.renderPageHero = renderPageHero;
window.initSharedComponents = initSharedComponents;
window.initSmoothScroll = initSmoothScroll;
window.capitalizeFirst = capitalizeFirst;
window.CONFIG = CONFIG;

// Auto-initialize when DOM is ready (optional)
document.addEventListener('DOMContentLoaded', () => {
    // Only auto-init if placeholder elements exist
    if (document.querySelector('#site-header') || document.querySelector('#site-footer')) {
        initSharedComponents();
    }

    initSmoothScroll();
});

// Export for module usage (ES6)
export {
    renderHeader,
    renderFooter,
    renderContact,
    renderPageHero,
    initSharedComponents,
    initSmoothScroll,
    capitalizeFirst,
    CONFIG
};
